import React, { useEffect, useRef, useState } from 'react';

import { Box, Divider, Paper, Stack, Typography } from '@mui/material';

import type { MediaImage, PropertyRoomTypeWithImages } from '../ImagesVideo';

import ImagesByRoomRow from './Row';
import {
  EMPTY_IMAGE_PUBLIC_IDS,
  ROOM_IMAGE_SLOT_COUNT,
  buildRoomImagesByKey,
  getRoomKey,
  pruneUnavailableRoomImages,
} from './utils';
import type { RoomImagesByKey } from './utils';

interface Props {
  excludedImagePublicIds?: Array<string>;
  hiddenImagePublicIds?: Array<string>;
  mediaImages: Array<MediaImage>;
  onAssignImagesToRoom: (roomName: string, selectedImages: Array<MediaImage>) => void;
  onRemoveImageFromRoom: (publicId: string) => void;
  onToggleHiddenImage: (publicId: string) => void;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
}

export default function ImagesByRoom({
  excludedImagePublicIds = EMPTY_IMAGE_PUBLIC_IDS,
  hiddenImagePublicIds = EMPTY_IMAGE_PUBLIC_IDS,
  mediaImages,
  onAssignImagesToRoom,
  onRemoveImageFromRoom,
  onToggleHiddenImage,
  propertyRoomTypes = [],
}: Props) {
  const [roomImagesByKey, setRoomImagesByKey] = useState<RoomImagesByKey>(() =>
    pruneUnavailableRoomImages(buildRoomImagesByKey(propertyRoomTypes, excludedImagePublicIds), mediaImages),
  );
  const previousPropertyRoomTypes = useRef(propertyRoomTypes);

  useEffect(() => {
    if (previousPropertyRoomTypes.current !== propertyRoomTypes) {
      previousPropertyRoomTypes.current = propertyRoomTypes;
      setRoomImagesByKey(
        pruneUnavailableRoomImages(buildRoomImagesByKey(propertyRoomTypes, excludedImagePublicIds), mediaImages),
      );
      return;
    }

    setRoomImagesByKey((currentRoomImagesByKey) => pruneUnavailableRoomImages(currentRoomImagesByKey, mediaImages));
  }, [excludedImagePublicIds, mediaImages, propertyRoomTypes]);

  if (propertyRoomTypes.length === 0) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={{ mt: 4, overflow: 'hidden' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={1}
        sx={{ px: 3, py: 2.5 }}
      >
        <Typography variant="h5">Images by room</Typography>
        <Typography variant="body2" color="text.secondary">
          Each row matches a room in{' '}
          <Box component="span" sx={{ fontWeight: 700 }}>
            Rooms
          </Box>
          . Up to {ROOM_IMAGE_SLOT_COUNT} carousel slots.
        </Typography>
      </Stack>
      <Divider />

      <Stack divider={<Divider />} sx={{ px: 3 }}>
        {propertyRoomTypes.map((room, index) => {
          const roomKey = getRoomKey(room, index);

          return (
            <ImagesByRoomRow
              key={roomKey}
              hiddenImagePublicIds={hiddenImagePublicIds}
              mediaImages={mediaImages}
              onAssignImagesToRoom={onAssignImagesToRoom}
              onRemoveImageFromRoom={onRemoveImageFromRoom}
              onToggleHiddenImage={onToggleHiddenImage}
              room={room}
              roomImages={roomImagesByKey[roomKey] ?? []}
              roomKey={roomKey}
              setRoomImagesByKey={setRoomImagesByKey}
            />
          );
        })}
      </Stack>
    </Paper>
  );
}
